const LEGACY_PARTNER_LIFECYCLE_STATUSES = [
  {
    key: 'active',
    label: 'Prospect',
    description: 'Entreprise identifiée mais relation non encore qualifiée.',
  },
  {
    key: 'inactive',
    label: 'Prospect qualifié',
    description: 'Entreprise correspondant à la cible commerciale avec un potentiel identifié.',
  },
  {
    key: 'active_client',
    label: 'Client actif',
    description: 'Client avec une relation commerciale ou un projet actif.',
  },
  {
    key: 'inactive_client',
    label: 'Client inactif',
    description: 'Ancien client sans activité récente.',
  },
  {
    key: 'lost',
    label: 'Perdu',
    description: 'Prospect ou relation commerciale abandonnée.',
  },
  {
    key: 'blocked',
    label: 'Bloqué',
    description: 'Entreprise avec laquelle toute nouvelle relation est interdite ou suspendue.',
  },
];

export const PARTNER_LIFECYCLE_STATUSES = LEGACY_PARTNER_LIFECYCLE_STATUSES
  .filter(({ key }) => ['active', 'inactive', 'blocked'].includes(key))
  .map((item) => item.key === 'active'
    ? { ...item, label: 'Actif', description: 'Partenaire utilisable normalement.' }
    : item.key === 'inactive'
      ? { ...item, label: 'Inactif', description: 'Partenaire conservé pour historique.' }
      : item);

export const PARTNER_LIFECYCLE_KEYS = new Set(
  PARTNER_LIFECYCLE_STATUSES.map(({ key }) => key),
);

export const PARTNER_SOURCES = [
  'Recommandation',
  'Client existant',
  'Site internet',
  'Appel entrant',
  'Prospection',
  'Appel d’offres',
  'Réseau professionnel',
  'Partenaire',
  'Autre',
];

export const PARTNER_ROLE_OPTIONS = [
  ['client', 'Client'], ['architect', 'Architecte'], ['project_manager', 'Maître d’œuvre'],
  ['owner', 'Maître d’ouvrage'], ['engineering_office', 'Bureau d’études'], ['economist', 'Économiste'],
  ['control_office', 'Bureau de contrôle'], ['coordinator', 'Coordonnateur'], ['installer', 'Installateur'],
  ['supplier', 'Fournisseur'], ['subcontractor', 'Sous-traitant'], ['public_body', 'Organisme public'],
].map(([key, label]) => ({ key, code: key, label }));

export const getPartnerRoleLabel = (role) => {
  const code = typeof role === 'string' ? role : role?.code;
  return PARTNER_ROLE_OPTIONS.find((option) => option.code === code)?.label
    || role?.label || code || '';
};

// Le rôle client reste lisible pour les données historiques, mais ne peut plus être affecté.
export const ASSIGNABLE_PARTNER_ROLE_OPTIONS = PARTNER_ROLE_OPTIONS.filter(({ code }) => code !== 'client');

export const PARTNER_RELATIONSHIP_STATUSES = [
  { key: 'prospect', label: 'Prospect' },
  { key: 'client', label: 'Client' },
  { key: 'client_inactif', label: 'Client inactif' },
];

const UNKNOWN_PARTNER_LIFECYCLE = {
  key: 'unknown',
  label: 'Non renseigné',
  description: 'Le statut administratif n’est pas renseigné ou reconnu.',
};

export const getPartnerLifecycle = (key) => (
  PARTNER_LIFECYCLE_STATUSES.find((lifecycle) => lifecycle.key === key)
  || { ...UNKNOWN_PARTNER_LIFECYCLE, value: key || '' }
);

export const EMPTY_PARTNER_FORM = {
  name: '',
  socialReason: '',
  legalName: '',
  lifecycleStatus: 'active',
  relationshipStatus: 'prospect',
  roles: [],
  originSource: '',
  employees: '',
  capital: '',
  vat: '',
  siren: '',
  siret: '',
  nafCode: '',
  domainId: '',
  subdomainId: '',
  website: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  address: '',
  city: '',
  regionOrState: '',
  postalCode: '',
  countryCode: 'FR',
  notes: '',
};

const valueOf = (source, camelKey, snakeKey = camelKey) => {
  if (source?.[camelKey] !== undefined && source?.[camelKey] !== null) return source[camelKey];
  if (source?.[snakeKey] !== undefined && source?.[snakeKey] !== null) return source[snakeKey];
  return '';
};

// Creation drafts can predate the separation of commercial and administrative status.
// Repair only the draft; existing partners keep their explicit administrative status.
export const restorePartnerCreationDraft = (stored = {}) => {
  const form = Object.keys(EMPTY_PARTNER_FORM).reduce((result, field) => ({
    ...result,
    [field]: stored?.[field] ?? EMPTY_PARTNER_FORM[field],
  }), {});
  if (!PARTNER_LIFECYCLE_KEYS.has(form.lifecycleStatus)) {
    form.lifecycleStatus = EMPTY_PARTNER_FORM.lifecycleStatus;
  }
  return form;
};

export const normalizePartnerContact = (contact = {}) => ({
  id: valueOf(contact, 'id'),
  firstName: valueOf(contact, 'firstName', 'first_name'),
  lastName: valueOf(contact, 'lastName', 'last_name'),
  contactName: valueOf(contact, 'contactName', 'contact_name'),
  agencyId: valueOf(contact, 'agencyId', 'agency_id'),
  agencyClientKey: valueOf(contact, 'agencyClientKey', 'agency_client_key'),
  role: valueOf(contact, 'role'),
  email: valueOf(contact, 'email'),
  phone: valueOf(contact, 'phone'),
  mobilePhone: valueOf(contact, 'mobilePhone', 'mobile_phone'),
  notes: valueOf(contact, 'notes'),
  _contactNameEdited: Boolean(valueOf(contact, 'contactName', 'contact_name')),
});

export const getContactDefaultName = (contact = {}) => (
  [contact.firstName, contact.lastName]
    .map((value) => String(value || '').trim())
    .filter(Boolean)
    .join(' ')
);

export const updatePartnerContactField = (contact = {}, name, value) => {
  if (name === 'contactName') return { ...contact, contactName: value, _contactNameEdited: true };
  const updated = { ...contact, [name]: value };
  if (['firstName', 'lastName'].includes(name) && !contact._contactNameEdited) {
    updated.contactName = getContactDefaultName(updated);
  }
  return updated;
};

export const normalizePartnerAgency = (agency = {}) => ({
  id: valueOf(agency, 'id'),
  clientKey: valueOf(agency, 'clientKey', 'client_key'),
  name: valueOf(agency, 'name'),
  socialReason: valueOf(agency, 'socialReason', 'social_reason'),
  addressLine1: valueOf(agency, 'addressLine1', 'address_line1'),
  addressLine2: valueOf(agency, 'addressLine2', 'address_line2'),
  address: valueOf(agency, 'address'),
  city: valueOf(agency, 'city'),
  regionOrState: valueOf(agency, 'regionOrState', 'region_or_state'),
  postalCode: valueOf(agency, 'postalCode', 'postal_code'),
  countryCode: valueOf(agency, 'countryCode', 'country_code') || 'FR',
  phone: valueOf(agency, 'phone'),
  email: valueOf(agency, 'email'),
  website: valueOf(agency, 'website'),
  notes: valueOf(agency, 'notes'),
});

export const normalizePartner = (partner = {}) => ({
  id: valueOf(partner, 'id'),
  parentCompanyId: valueOf(partner, 'parentCompanyId', 'parent_company_id'),
  ownerUserId: valueOf(partner, 'ownerUserId', 'owner_user_id'),
  type: valueOf(partner, 'type'),
  roles: (Array.isArray(partner.roles) ? partner.roles : []).map((role) => typeof role === 'string' ? role : role.code),
  branchType: valueOf(partner, 'branchType', 'branch_type'),
  ...buildPartnerForm(partner),
  lastContactAt: valueOf(partner, 'lastContactAt', 'last_contact_at'),
  nextFollowUpAt: valueOf(partner, 'nextFollowUpAt', 'next_follow_up_at'),
  lastVerification: valueOf(partner, 'lastVerification', 'last_verification'),
  contacts: (Array.isArray(partner.contacts) ? partner.contacts : partner.Contacts || [])
    .map(normalizePartnerContact),
  agencies: (Array.isArray(partner.agencies) ? partner.agencies : [])
    .map(normalizePartnerAgency),
});

export function buildPartnerForm(partner = {}) {
  return {
    name: valueOf(partner, 'name'),
    socialReason: valueOf(partner, 'socialReason', 'social_reason'),
    legalName: valueOf(partner, 'legalName', 'legal_name'),
    lifecycleStatus: valueOf(partner, 'lifecycleStatus', 'lifecycle_status') || 'active',
    relationshipStatus: valueOf(partner, 'relationshipStatus', 'relationship_status') || 'prospect',
    relationshipSinceAt: valueOf(partner, 'relationshipSinceAt', 'relationship_since_at'),
    becameClientAt: valueOf(partner, 'becameClientAt', 'became_client_at'),
    lastCommercialActivityAt: valueOf(partner, 'lastCommercialActivityAt', 'last_commercial_activity_at'),
    marketingPriority: valueOf(partner, 'marketingPriority', 'marketing_priority') || 'normal',
    originSource: valueOf(partner, 'originSource', 'origin_source'),
    roles: (Array.isArray(partner.roles) ? partner.roles : []).map((role) => typeof role === 'string' ? role : role.code),
    employees: valueOf(partner, 'employees'),
    capital: valueOf(partner, 'capital'),
    vat: valueOf(partner, 'vat'),
    siren: valueOf(partner, 'siren'),
    siret: valueOf(partner, 'siret'),
    nafCode: valueOf(partner, 'nafCode', 'naf_code'),
    domainId: valueOf(partner, 'domainId', 'domain_id'),
    subdomainId: valueOf(partner, 'subdomainId', 'subdomain_id'),
    website: valueOf(partner, 'website'),
    email: valueOf(partner, 'email'),
    phone: valueOf(partner, 'phone'),
    addressLine1: valueOf(partner, 'addressLine1', 'address_line1'),
    addressLine2: valueOf(partner, 'addressLine2', 'address_line2'),
    address: valueOf(partner, 'address'),
    city: valueOf(partner, 'city'),
    regionOrState: valueOf(partner, 'regionOrState', 'region_or_state'),
    postalCode: valueOf(partner, 'postalCode', 'postal_code'),
    countryCode: valueOf(partner, 'countryCode', 'country_code') || 'FR',
    notes: valueOf(partner, 'notes'),
  };
}

const PARTNER_PAYLOAD_FIELDS = [
  ['name'], ['socialReason', 'social_reason'], ['legalName', 'legal_name'],
  ['lifecycleStatus', 'lifecycle_status'], ['relationshipStatus', 'relationship_status'],
  ['relationshipSinceAt', 'relationship_since_at'], ['marketingPriority', 'marketing_priority'],
  ['originSource', 'origin_source'], ['roles'], ['employees'], ['capital'], ['vat'], ['siren'], ['siret'],
  ['nafCode', 'naf_code'], ['domainId', 'domain_id'], ['subdomainId', 'subdomain_id'],
  ['website'], ['email'], ['phone'], ['addressLine1', 'address_line1'],
  ['addressLine2', 'address_line2'], ['address'], ['city'],
  ['regionOrState', 'region_or_state'], ['postalCode', 'postal_code'],
  ['countryCode', 'country_code'], ['notes'], ['type'], ['branchType', 'branch_type'],
];

export const buildPartnerPayload = (partner = {}) => PARTNER_PAYLOAD_FIELDS.reduce(
  (payload, [camelKey, snakeKey = camelKey]) => {
    if (Object.prototype.hasOwnProperty.call(partner, camelKey)
      || Object.prototype.hasOwnProperty.call(partner, snakeKey)) {
      payload[camelKey] = valueOf(partner, camelKey, snakeKey);
    }
    return payload;
  },
  {},
);

export const getPartnerDisplayName = (partner = {}) => (
  valueOf(partner, 'socialReason', 'social_reason')
  || valueOf(partner, 'name')
  || valueOf(partner, 'legalName', 'legal_name')
  || 'Partenaire sans nom'
);

export const getPartnerValue = (partner, field) => normalizePartner(partner)[field] ?? '';
